import React, { useState } from "react";

import {
  Table,
  Form,
  Input,
  message as antdMessage,
  Alert,
  Skeleton,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { Plus } from "lucide-react";

import type { ColumnsType } from "antd/es/table";

import FormModal from "../../common/modal/FormModal";

import { Term } from "../../../bitmaker/schemas/Billing/Term";

import { useTerms } from "../../../hooks/useTerms";
import { useApiError } from "../../../hooks/useApiError";

import { HTTPMethod, methodMap } from "../../../services/BitmakerApp";

import { MyFormItem } from "../MyFormItem";

export default function TermsManagement() {
  const { terms, loadingTerms, fetchTermsError, refetchTerms } = useTerms();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const [editingTerm, setEditingTerm] = useState<Term | null>(null);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { handleAPIError } = useApiError();

  const handleEditModal = (record: Term) => {
    setEditingTerm(record);
    form.setFieldsValue({
      name: record.name.trim(),
      content: record.content.trim(),
    });
    setIsModalOpen(true);
    setSubmitError(null);
  };

  const handleAddModal = () => {
    setEditingTerm(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleCancelModal = () => {
    setSubmitError(null);
    setIsModalOpen(false);
  };

  const getTermErrorMessage = (status: number | null, isEditing: boolean) => {
    if (status === 409) {
      return "An invoice term with this name already exists.";
    }

    return `Error ${isEditing ? "updating" : "creating"
      } invoice term. Please try again.`;
  };

  const handleSubmit = (values: { name: string; content: string }) => {
    if (isSubmitting) return;

    setSubmitError(null);
    setIsSubmitting(true);

    const isEditing = editingTerm !== null;

    const dataToSend = {
      name: values.name.trim(),
      content: values.content.trim(),
    };

    const endpoint = isEditing
      ? { method: "put", url: `/billing/settings/terms/${editingTerm?.id}` }
      : { method: "post", url: "/billing/settings/terms" };

    const method = endpoint.method as HTTPMethod;

    methodMap[method](endpoint.url, dataToSend, (response) => {
      const { isError, status } = handleAPIError(response);

      if (isError) {
        const errorMessage = getTermErrorMessage(status, isEditing);
        setSubmitError(errorMessage);
        setIsSubmitting(false);
        return;
      }

      antdMessage.success(
        `Invoice term "${response.data.name}" ${isEditing ? "updated" : "created"
        } successfully!`,
      );

      setIsModalOpen(false);
      setEditingTerm(null);
      refetchTerms();
      setIsSubmitting(false);
    });
  };

  const columns: ColumnsType<Term> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      render: (name: string) => (
        <span className="font-medium text-gray-700">{name}</span>
      ),
    },
    {
      title: "Content",
      dataIndex: "content",
      key: "content",
      width: "60%",
      render: (content: string) => (
        <span className="text-gray-500" style={{ whiteSpace: "pre-wrap" }}>
          {content}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <button
          type="button"
          onClick={() => handleEditModal(record)}
          className="btn btn-small btn-indigo"
          disabled={loadingTerms}
        >
          <EditOutlined size={14} />
          <span>Edit</span>
        </button>
      ),
    },
  ];

  const isInitialLoading = loadingTerms && terms.length === 0;
  const fetchError = !!fetchTermsError;

  return (
    <div>
      {/* Header + primary action */}
      <div className="flex justify-between items-start gap-4 mb-6">
        <div className="flex flex-col gap-1 min-w-0">
          <h2 className="text-2xl text-gray-800">Invoice Terms</h2>
          <p className="text-gray-600 text-sm">
            Define the default text blocks shown on invoices (e.g. bank details,
            legal wording).
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddModal}
          className="btn btn-green-2 shrink-0"
          disabled={loadingTerms}
        >
          <Plus size={16} />
          <span>Add Invoice Term</span>
        </button>
      </div>

      {/* Error */}
      {fetchError && (
        <div className="mb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load invoice terms"
            description="Something went wrong while fetching data. Please try again."
            action={
              <button
                type="button"
                className="btn btn-gray"
                onClick={refetchTerms}
                disabled={loadingTerms}
              >
                Retry
              </button>
            }
          />
        </div>
      )}

      {/* Table / Loading */}
      {isInitialLoading ? (
        <div className="mt-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="py-2">
              <Skeleton
                active
                title={false}
                paragraph={{ rows: 1, width: "100%" }}
              />
            </div>
          ))}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={terms}
          rowKey="id"
          loading={loadingTerms}
          pagination={false}
          rowClassName={() => "hover:bg-gray-50 transition-colors"}
          locale={{
            emptyText: fetchError ? (
              "Unable to load invoice terms"
            ) : (
              <div className="py-10 flex flex-col items-center justify-center gap-3 text-center">
                <div className="text-gray-500 text-sm">
                  You don't have any invoice terms yet
                </div>
                <button
                  type="button"
                  className="btn btn-gray"
                  onClick={handleAddModal}
                  disabled={loadingTerms}
                >
                  <Plus size={16} />
                  <span>Create Invoice Term</span>
                </button>
              </div>
            ),
          }}
          bordered
        />
      )}

      {/* Add/Edit Term Form Modal */}
      <FormModal
        title={
          editingTerm
            ? `Edit Invoice Term: ${editingTerm.name}`
            : "Add New Invoice Term"
        }
        open={isModalOpen}
        onCancel={handleCancelModal}
        form={form}
        formId="addTermForm"
        onFinish={handleSubmit}
        isSubmitting={isSubmitting}
        submitLabel={editingTerm ? "Update" : "Save"}
        error={submitError}
        afterClose={() => {
          form.resetFields();
          setEditingTerm(null);
          setSubmitError(null);
        }}
      >
        <MyFormItem
          label="Term Name"
          name="name"
          rules={[{ required: true, message: "Please enter the term name." }]}
        >
          <Input
            placeholder="BITMAKER E.I.R.L. (Wise USD)"
            onChange={() => submitError && setSubmitError(null)}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== e.target.value) {
                form.setFieldsValue({ name: value });
              }
            }}
          />
        </MyFormItem>

        <MyFormItem
          label="Content"
          name="content"
          rules={[
            {
              required: true,
              message: "Please enter the term description.",
            },
          ]}
        >
          <Input.TextArea
            rows={8}
            placeholder="Explain the payment term details..."
            onChange={() => submitError && setSubmitError(null)}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== e.target.value) {
                form.setFieldsValue({ content: value });
              }
            }}
          />
        </MyFormItem>
      </FormModal>
    </div>
  );
}
